/** URL builders for the voice-profile routes (api-scriptix #4171).
 *
 * Kept out of `endpoints.ts`: that module is a single 1000-line object whose
 * return type has to list every key, so a feature's worth of routes is far
 * easier to read — and to delete — in its own file. */

import { URL_EXTENSION } from '@utils/constants/app-constants.ts';

const ORG_SPEAKERS = `${URL_EXTENSION}/organization/speakers`;

const documentBase = (sessionId: string, documentId: string) =>
  `${URL_EXTENSION}/speech-to-text/session/${encodeURIComponent(
    sessionId
  )}/document/${encodeURIComponent(documentId)}`;

/** Organization-directory routes: readiness and per-speaker samples. */
export const VOICE_PROFILE_URL = {
  /** Readiness, permission and UI limits for the session's organization. */
  capabilities: () => `${ORG_SPEAKERS}/voice-profiles/capabilities`,

  /** List one speaker's enrolled samples (metadata only). */
  samples: (speakerId: number) => `${ORG_SPEAKERS}/${speakerId}/voice-profiles`,

  /** Append a sample, keeping the speaker's existing ones. */
  addSample: (speakerId: number, label?: string | null) => {
    const base = `${ORG_SPEAKERS}/${speakerId}/voice-profiles`;
    return label ? `${base}?label=${encodeURIComponent(label)}` : base;
  },

  /** Replace one named sample — never a guess at "the" profile. */
  replaceSample: (
    speakerId: number,
    profileId: number,
    label?: string | null
  ) => {
    const base = `${ORG_SPEAKERS}/${speakerId}/voice-profiles/${profileId}`;
    return label ? `${base}?label=${encodeURIComponent(label)}` : base;
  },

  /** Delete one sample. */
  deleteSample: (speakerId: number, profileId: number) =>
    `${ORG_SPEAKERS}/${speakerId}/voice-profiles/${profileId}`,

  /** Explicitly clear every sample for the speaker. */
  clearSamples: (speakerId: number) =>
    `${ORG_SPEAKERS}/${speakerId}/voice-profiles`
} as const;

/** Document-scoped routes: review, assignment and sample selection. */
export const VOICE_IDENTIFICATION_URL = {
  /** Original diarization labels mapped to source-audio milliseconds. */
  timeline: (sessionId: string, documentId: string) =>
    `${documentBase(sessionId, documentId)}/voice-identification-timeline`,

  /** Job status plus per-label suggestions and overrides. */
  identifications: (sessionId: string, documentId: string) =>
    `${documentBase(sessionId, documentId)}/voice-identifications`,

  /** Choose a directory person, or null for an explicit unknown. */
  override: (sessionId: string, documentId: string, speakerLabel: string) =>
    `${documentBase(sessionId, documentId)}/voice-identifications/${encodeURIComponent(
      speakerLabel
    )}`,

  /** Clear the human decision and fall back to the suggestion. */
  clearOverride: (
    sessionId: string,
    documentId: string,
    speakerLabel: string
  ) =>
    `${documentBase(sessionId, documentId)}/voice-identifications/${encodeURIComponent(
      speakerLabel
    )}/override`,

  /** Ask the backend's clean-speech planner for an initial selection. */
  samplePlan: (sessionId: string, documentId: string) =>
    `${documentBase(sessionId, documentId)}/voice-profile-sample-plan`,

  /** Cut the selection server-side and return a disposable preview WAV. */
  samplePreview: (sessionId: string, documentId: string) =>
    `${documentBase(sessionId, documentId)}/voice-profile-sample`
} as const;
